import { useEffect } from 'react';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import P_CollapsibleSection from '../common/collapsibleSection/P_CollapsibleSection';
import Button from 'src/ui/wards/common/button/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWaProductStore } from 'src/api/wa/store/WaProductStore';
import LinkButton from 'src/ui/wards/common/button/LinkButton';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import InlineAlert from 'src/ui/wards/common/inlineAlert/InlineAlert';
import useWaProductService from 'src/api/wa/service/WaProductService';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import DataTable from 'react-data-table-component';
import { productTableStyle, productWorkRecordsColumns } from './productWorkRecordsColumns';
import { formatJpnDate } from 'src/common/formatJpnDate/formatJpnDate';
import { FormRow } from 'src/ui/wards/common/form/FormRow';

const toDisplayText = (value?: string | null) => {
  if (value === null || value === undefined) return '';

  // Filter out 'null' strings from comma-separated values
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'null') return '';

  // Handle comma-separated values with potential nulls (e.g., "null, null, null")
  if (trimmed.includes(',')) {
    const filtered = trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && s !== 'null')
      .join(', ');
    return filtered;
  }

  return trimmed;
};

const P6_3_DispatchOrderDetailScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedProduct, setSelectedProduct, productDetails } = useWaProductStore();
  const { fetchProductDetails, fetchProductMasterDetails } = useWaProductService();
  const { selectedWorkplace } = useWaWorkplaceStore();

  useEffect(() => {
    if (selectedProduct && selectedWorkplace) {
      if (selectedProduct.disasterId) {
        fetchProductDetails(selectedProduct, selectedWorkplace);
      } else {
        fetchProductMasterDetails(selectedProduct, selectedWorkplace);
      }
    }
  }, [fetchProductDetails, fetchProductMasterDetails, selectedProduct, selectedWorkplace]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Cog6ToothIcon className="size-10 text-blue-600" />
            <Cog6ToothIcon className="absolute left-4 top-4 size-6 text-green-600" />
          </div>
          <div>
            <div className="text-sm text-gray-600">
              {t('wards.productDetails.product.productNumber')}
            </div>
            <div className="text-3xl font-bold">{selectedProduct?.productNumber}</div>
          </div>
        </div>
        <div className="flex justify-end space-x-2">
          <Button
            title={t('wards.productDetails.statusChange')}
            onClick={() => {
              wardsNavigate(navigate, WaScreensMap[WA_Screen.EditProduct].path);
            }}
          />
          <Button
            title={t('wards.common.back')}
            onClick={() => {
              navigate(-1);
            }}
          />
        </div>
      </div>
      <InlineAlert />
      <P_CollapsibleSection
        title={t('wards.productDetails.productNumberDetails')}
        accentClassName="border-amber-800"
      >
        <FormRow title={t('wards.productDetails.product.productNumber')}>
          {selectedProduct?.productNumber}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.machineNumber')}>
          {selectedProduct?.unit}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.contract')}>
          {selectedProduct?.contractName}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.type')}>{selectedProduct?.model}</FormRow>
        <FormRow title={t('wards.productDetails.product.organizationCode')}>
          {productDetails?.organizationCode}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.branchCode')}>
          {productDetails?.branchCode}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.salesOfficeCode')}>
          {productDetails?.salesOfficeCode}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.sectionCode')}>
          {productDetails?.sectionCode}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.squadCode')}>
          {productDetails?.squadCode}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.priorityPoints')}>
          {toDisplayText(productDetails?.priorityPoints)}
        </FormRow>
        <FormRow title={t('wards.productDetails.product.safetyPoints')}>
          {toDisplayText(productDetails?.safetyPoints)}
        </FormRow>
      </P_CollapsibleSection>
      <P_CollapsibleSection
        title={t('wards.productDetails.history.title')}
        accentClassName="border-amber-800"
      >
        <div className="my-2">
          {productDetails?.workRecords && productDetails?.workRecords.length !== 0 && (
            <DataTable
              className="size-full"
              noDataComponent={t('wards.common.noResults')}
              columns={productWorkRecordsColumns(t)}
              data={productDetails.workRecords}
              customStyles={productTableStyle}
              defaultSortFieldId="productNumber"
              defaultSortAsc={true}
              dense
            />
          )}
        </div>
      </P_CollapsibleSection>
      <P_CollapsibleSection
        title={t('wards.productDetails.wideArea.title')}
        accentClassName="border-amber-800"
      >
        <FormRow title={t('wards.productDetails.wideArea.disasterId')}>
          {productDetails?.disasterId}
        </FormRow>
        <FormRow title={t('wards.productDetails.wideArea.state')}>
          {productDetails?.statusId
            ? t(getDetailStatusTranslationKey(productDetails?.statusId))
            : ''}
        </FormRow>
        <FormRow title={t('wards.productDetails.wideArea.handler')}>
          {productDetails?.addressedName}
        </FormRow>
        <FormRow title={t('wards.productDetails.wideArea.handlingHours')}>
          {productDetails?.addressedOn ? formatJpnDate({ iso: productDetails?.addressedOn }) : ''}
        </FormRow>
        <FormRow title={t('wards.productDetails.wideArea.lastUpdate')}>
          {productDetails?.lastUpdateDate
            ? formatJpnDate({ iso: productDetails?.lastUpdateDate })
            : ''}
        </FormRow>
        <FormRow title={t('wards.productDetails.wideArea.operationId')}>
          <LinkButton
            title={selectedWorkplace?.name || ''}
            onClick={() => {
              setSelectedProduct(selectedProduct);
              wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplaceDetails].path);
            }}
          />
        </FormRow>
      </P_CollapsibleSection>
      <div className="flex justify-center space-x-4">
        <Button
          title={t('wards.productDetails.statusChange')}
          onClick={() => {
            wardsNavigate(navigate, WaScreensMap[WA_Screen.EditProduct].path);
          }}
        />
        <Button
          title={t('wards.common.back')}
          onClick={() => {
            navigate(-1);
          }}
        />
      </div>
    </div>
  );
};

export default P6_3_DispatchOrderDetailScreen;
